import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import OrderCard from "@/components/OrderCard";
import { Plus, Radar, Zap, RefreshCw, MapPin, ShoppingCart, Store, Repeat, Gift, Wrench, Target, ShieldCheck } from "lucide-react";
import { logger } from "@/lib/logger";
import { toast } from "sonner";
import useGeoLocation from "@/lib/useGeoLocation";
import useFeedNotifications from "@/lib/useFeedNotifications";
import { MONETIZATION_ENABLED } from "@/lib/features";
import { pushSupported, currentPushStatus, enablePush } from "@/lib/push";

export default function Home() {
  const { user, updateRole } = useAuth();
  const nav = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [prefsConfigured, setPrefsConfigured] = useState(true);
  const [verifiedOnly, setVerifiedOnly] = useState(() => {
    try { return localStorage.getItem("rivendas_verified_only") === "1"; } catch { return false; }
  });
  const [pushNudge, setPushNudge] = useState(false);
  const scope = user?.role === "seller" ? "feed" : "mine";
  const geo = useGeoLocation();

  // Real-time notifications for sellers: prepend the new order & toast
  useFeedNotifications(user?.role === "seller", (order) => {
    setOrders((prev) => {
      if (prev.some((o) => o.order_id === order.order_id)) return prev;
      return [order, ...prev];
    });
    toast.success(`🔥 Novo pedido: ${order.title}`, {
      description: order.distance_km != null ? `${order.distance_km} km · ${order.city}` : order.city,
    });
  });

  // Check whether the seller has configured their specialties
  useEffect(() => {
    if (user?.role !== "seller") { setPrefsConfigured(true); return; }
    api.get("/me/preferences")
      .then((r) => setPrefsConfigured(!!r.data?.configured))
      .catch((err) => logger.warn("home: prefs check", err?.message));
  }, [user?.role]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = scope === "feed" && verifiedOnly ? `?scope=${scope}&verified_only=true` : `?scope=${scope}`;
      const { data } = await api.get(`/orders${qs}`);
      setOrders(data);
    } catch (err) {
      logger.error("home: failed to load orders", err);
    }
    setLoading(false);
  }, [scope, verifiedOnly]);

  useEffect(() => {
    load();
    // Poll every 15s for near-real-time updates
    const iv = setInterval(load, 15000);
    return () => clearInterval(iv);
  }, [load]);

  // Contextual push nudge: buyer with at least 1 order but has never
  // enabled notifications AND hasn't dismissed the nudge yet.
  useEffect(() => {
    if (!user || user.role === "seller") return;
    if (!pushSupported()) return;
    try {
      if (localStorage.getItem("rivendas_push_nudge_dismissed") === "1") return;
    } catch { /* ignore */ }
    if (orders.length === 0) return;
    currentPushStatus().then((s) => {
      if (!s.subscribed && s.permission !== "denied") setPushNudge(true);
    });
  }, [orders.length, user]);

  const toggleVerified = () => {
    setVerifiedOnly((v) => {
      const next = !v;
      try { localStorage.setItem("rivendas_verified_only", next ? "1" : "0"); } catch { /* ignore */ }
      return next;
    });
  };

  const acceptPushNudge = async () => {
    try {
      await enablePush();
      toast.success("Notificações ativadas — vamos avisar quando alguém responder");
    } catch (e) {
      toast.error(e?.message || "Falha ao ativar");
    } finally {
      setPushNudge(false);
      try { localStorage.setItem("rivendas_push_nudge_dismissed", "1"); } catch { /* ignore */ }
    }
  };

  const dismissPushNudge = () => {
    setPushNudge(false);
    try { localStorage.setItem("rivendas_push_nudge_dismissed", "1"); } catch { /* ignore */ }
  };

  if (!user) return null;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 pb-24">
      {/* Greeting */}
      <div className="mb-6">
        <p className="text-sm text-muted-foreground">Olá, {user.name?.split(" ")[0]}</p>
        <h1 className="font-heading font-black text-3xl md:text-4xl tracking-tight mt-1">
          {user.role === "seller"
            ? "Pedidos disponíveis"
            : "Bem-vindo(a) ao Rivendas"}
        </h1>
      </div>

      {/* Unified 4-way action hero — always visible for both roles.
          Clicking Vender switches to seller mode; the other 3 ensure buyer mode + open CreateOrder pre-selected. */}
      <div className="mb-6" data-testid="home-actions">
        <p className="text-xs uppercase tracking-widest font-bold text-muted-foreground mb-3">
          O que você quer fazer?
        </p>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <button
            type="button"
            data-testid="home-cta-comprar"
            onClick={async () => {
              if (user.role !== "buyer") { try { await updateRole("buyer"); } catch (e) { logger.warn("home: role switch buyer", e?.message); } }
              nav("/create?type=compra");
            }}
            className={`relative overflow-hidden text-left rounded-2xl p-4 md:p-5 flex flex-col items-start gap-2 transition-all ${
              user.role === "buyer"
                ? "bg-primary text-primary-foreground cta-pulse hover:opacity-95"
                : "bg-card border-2 border-primary/40 hover:border-primary hover:bg-primary/5"
            }`}
          >
            <ShoppingCart size={22} className={user.role === "buyer" ? "" : "text-primary"} />
            <span className="font-heading font-black text-lg md:text-xl leading-none">Comprar</span>
            <span className={`text-[11px] leading-tight ${user.role === "buyer" ? "opacity-80" : "text-muted-foreground"}`}>
              Pago pelo item
            </span>
          </button>

          <button
            type="button"
            data-testid="home-cta-vender"
            onClick={async () => {
              if (user.role !== "seller") {
                try { await updateRole("seller"); toast.success("Modo vendedor ativado"); } catch (e) { logger.warn("home: role switch seller", e?.message); }
              }
            }}
            className={`relative text-left rounded-2xl p-4 md:p-5 flex flex-col items-start gap-2 transition-all ${
              user.role === "seller"
                ? "bg-primary text-primary-foreground cta-pulse hover:opacity-95"
                : "bg-card border-2 border-primary/40 hover:border-primary hover:bg-primary/5"
            }`}
          >
            <Store size={22} className={user.role === "seller" ? "" : "text-primary"} />
            <span className="font-heading font-black text-lg md:text-xl leading-none">Vender</span>
            <span className={`text-[11px] leading-tight ${user.role === "seller" ? "opacity-80" : "text-muted-foreground"}`}>
              Disputar pedidos
            </span>
          </button>

          <button
            type="button"
            data-testid="home-cta-servico"
            onClick={async () => {
              if (user.role !== "buyer") { try { await updateRole("buyer"); } catch (e) { logger.warn("home: role switch buyer", e?.message); } }
              nav("/create?type=servico");
            }}
            className="relative text-left bg-card border-2 border-primary/40 rounded-2xl p-4 md:p-5 flex flex-col items-start gap-2 hover:border-primary hover:bg-primary/5 transition-colors"
          >
            <Wrench size={22} className="text-primary" />
            <span className="font-heading font-black text-lg md:text-xl leading-none">Serviços</span>
            <span className="text-[11px] text-muted-foreground leading-tight">Preciso de um profissional</span>
          </button>

          <button
            type="button"
            data-testid="home-cta-trocar"
            onClick={async () => {
              if (user.role !== "buyer") { try { await updateRole("buyer"); } catch (e) { logger.warn("home: role switch buyer", e?.message); } }
              nav("/create?type=permuta");
            }}
            className="relative text-left bg-card border-2 border-primary/40 rounded-2xl p-4 md:p-5 flex flex-col items-start gap-2 hover:border-primary hover:bg-primary/5 transition-colors"
          >
            <Repeat size={22} className="text-primary" />
            <span className="font-heading font-black text-lg md:text-xl leading-none">Trocar</span>
            <span className="text-[11px] text-muted-foreground leading-tight">Permuta sem dinheiro</span>
          </button>

          <button
            type="button"
            data-testid="home-cta-doar"
            onClick={async () => {
              if (user.role !== "buyer") { try { await updateRole("buyer"); } catch (e) { logger.warn("home: role switch buyer", e?.message); } }
              nav("/create?type=doacao&direction=dar");
            }}
            className="relative text-left bg-card border-2 border-primary/40 rounded-2xl p-4 md:p-5 flex flex-col items-start gap-2 hover:border-primary hover:bg-primary/5 transition-colors"
          >
            <Gift size={22} className="text-primary" />
            <span className="font-heading font-black text-lg md:text-xl leading-none">Doar</span>
            <span className="text-[11px] text-muted-foreground leading-tight">Ofereça gratuitamente</span>
          </button>
        </div>
      </div>

      {/* Buyer free-boost banner (only when monetization is enabled) */}
      {MONETIZATION_ENABLED && user.role === "buyer" && user.has_free_boost && (
        <div
          data-testid="free-boost-banner"
          className="mb-6 bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/40 rounded-2xl p-4 flex items-center gap-3"
        >
          <span className="text-3xl">🎁</span>
          <div className="flex-1">
            <p className="font-heading font-bold text-lg">Você tem um destaque grátis!</p>
            <p className="text-xs text-muted-foreground">
              Seu próximo pedido aparece no topo do feed dos vendedores.
            </p>
          </div>
        </div>
      )}

      {/* Seller specialties nudge — smart notifications */}
      {user.role === "seller" && !prefsConfigured && (
        <Link
          to="/especialidades"
          data-testid="prefs-nudge"
          className="mb-6 flex items-center gap-3 bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/40 rounded-2xl p-4 hover:border-primary transition-colors"
        >
          <div className="w-11 h-11 rounded-xl bg-primary/20 border border-primary/40 flex items-center justify-center shrink-0">
            <Target size={20} className="text-primary" />
          </div>
          <div className="flex-1">
            <p className="font-heading font-bold text-lg leading-tight">Configure suas especialidades</p>
            <p className="text-xs text-muted-foreground mt-0.5">
              Escolha categorias, cidades e palavras-chave — receba só o que combina com você.
            </p>
          </div>
        </Link>
      )}

      {/* Seller stats */}
      {user.role === "seller" && (
        <>
          {!user.lat && geo.status !== "granted" && (
            <div
              data-testid="geo-prompt"
              className="mb-4 bg-card border border-primary/40 rounded-2xl p-4 flex items-center gap-3"
            >
              <MapPin size={22} className="text-primary shrink-0" />
              <div className="flex-1 min-w-0">
                <p className="font-heading font-bold text-sm">Ative a localização</p>
                <p className="text-xs text-muted-foreground">
                  Receba só pedidos dentro do seu raio de atendimento.
                </p>
              </div>
              <Button
                onClick={() => geo.request().catch(() => toast.error("Permissão negada"))}
                data-testid="geo-request-btn"
                size="sm"
                className="shrink-0 font-bold"
              >
                Ativar
              </Button>
            </div>
          )}
          <div className="grid grid-cols-3 gap-3 mb-6">
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Vendas</p>
            <p className="font-heading font-black text-2xl mt-1">{user.total_sales || 0}</p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Nota</p>
            <p className="font-heading font-black text-2xl mt-1 text-amber-500">
              {user.rating_avg > 0 ? user.rating_avg.toFixed(1) : "—"}
              <span className="text-xs text-muted-foreground ml-1 font-normal">
                ({user.rating_count || 0})
              </span>
            </p>
          </div>
          <div className="bg-card border border-border rounded-xl p-4">
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">Ativos</p>
            <p className="font-heading font-black text-2xl mt-1 text-primary">
              {orders.filter((o) => o.status === "accepted" && o.accepted_by === user.user_id).length}
            </p>
          </div>
        </div>
        </>
      )}

      {/* Section title */}
      <div className="flex items-center justify-between mb-3 gap-2 flex-wrap">
        <h3 className="font-heading font-bold text-xl flex items-center gap-2">
          {user.role === "seller" ? (
            <><Radar size={18} className="text-primary" /> Feed em tempo real</>
          ) : (
            <><Zap size={18} className="text-primary" /> Meus pedidos</>
          )}
        </h3>
        <div className="flex items-center gap-2">
          {user.role === "seller" && (
            <button
              data-testid="verified-only-toggle"
              onClick={toggleVerified}
              className={`inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg border transition-colors ${
                verifiedOnly
                  ? "bg-emerald-500/15 border-emerald-500/50 text-emerald-500"
                  : "bg-secondary border-transparent text-muted-foreground hover:text-foreground"
              }`}
              title="Mostrar só pedidos de compradores com documento verificado"
            >
              <ShieldCheck size={14} strokeWidth={3} />
              Só verificados
            </button>
          )}
          <button
            data-testid="refresh-orders"
            onClick={load}
            className="p-2 rounded-lg hover:bg-secondary transition-colors"
            aria-label="Atualizar"
          >
            <RefreshCw size={16} className={loading ? "animate-spin" : ""} />
          </button>
        </div>
      </div>

      {pushNudge && (
        <div
          data-testid="push-nudge"
          className="mb-3 bg-gradient-to-br from-primary/15 to-primary/5 border border-primary/30 rounded-xl p-3 flex items-center gap-3"
        >
          <div className="w-9 h-9 rounded-lg bg-primary/15 text-primary flex items-center justify-center shrink-0">
            <Zap size={16} />
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-heading font-bold text-sm">Ative os avisos rápidos</p>
            <p className="text-xs text-muted-foreground">Aparece um ping assim que alguém responde seu pedido.</p>
          </div>
          <button
            data-testid="push-nudge-skip"
            onClick={dismissPushNudge}
            className="text-xs text-muted-foreground hover:text-foreground px-2"
          >
            Agora não
          </button>
          <button
            data-testid="push-nudge-accept"
            onClick={acceptPushNudge}
            className="text-xs font-bold px-3 py-2 rounded-lg bg-primary text-primary-foreground"
          >
            Ativar
          </button>
        </div>
      )}

      {loading && orders.length === 0 ? (
        <div className="text-center text-muted-foreground py-12">Carregando...</div>
      ) : orders.length === 0 ? (
        <div className="text-center py-12 border border-dashed border-border rounded-xl">
          <p className="text-muted-foreground">
            {user.role === "seller"
              ? "Nenhum pedido disponível no momento. Volte em instantes."
              : "Você ainda não criou nenhum pedido."}
          </p>
          {user.role === "buyer" && (
            <Link to="/create">
              <Button className="mt-4" data-testid="empty-create-btn">
                <Plus size={16} className="mr-1" /> Criar primeiro pedido
              </Button>
            </Link>
          )}
        </div>
      ) : (
        <div className="space-y-3">
          {orders.map((o) => (
            <OrderCard
              key={o.order_id}
              order={o}
              actionLabel={user.role === "seller" ? "Aceitar" : "Abrir"}
              testId={`order-card-${o.order_id}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
